import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// Material UI
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, IconButton, TextField, Tooltip, Modal, Box, Typography,
  Fade, Paper
} from "@mui/material";
import { tableCellClasses } from "@mui/material/TableCell";
import { styled } from "@mui/material/styles";
import DeleteTwoToneIcon from '@mui/icons-material/DeleteTwoTone';
import InfoIcon from "@mui/icons-material/Info";
import { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";

// Interface
import { ITask, IModal } from "../../interface/task";
import { ILot } from "../../interface/lot";

// Redux
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../redux/reducers/index";
import {
  addTask, changeValue, sessionEnd, initTasks,
  delete_tasks, calculAll, calculTCosts
} from "../../redux/actions/index";

// Tools & Components
import { getOne } from "../../fetchs/get";
import { fetchPutProtect } from "../../fetchs/put";
import useWindowDimensions from "../../tools/useWindowDimensions";
import AlerteDialog from "../AlerteDialog/AlerteDialog";
import { useParams } from "react-router-dom";

// --- Styled Components ---

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#f8fafc",
    color: "#64748b",
    fontWeight: 700,
    fontSize: "0.75rem",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    borderBottom: "2px solid #e2e8f0",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: "0.85rem",
    padding: "4px 8px",
  },
}));

const InputField = styled(TextField)({
  '& .MuiFilledInput-root': {
    backgroundColor: 'transparent',
    transition: 'all 0.2s',
    '&:hover': { backgroundColor: '#f1f5f9' },
    '&.Mui-focused': { backgroundColor: '#fff', boxShadow: 'inset 0 0 0 2px #3b82f6' },
    '&:before, &:after': { display: 'none' },
    borderRadius: '4px',
  },
  '& input': {
    padding: '8px !important',
    textAlign: 'center',
    fontWeight: 500,
  }
});

const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#1e293b",
    color: "#fff",
    maxWidth: 220,
    fontSize: theme.typography.pxToRem(12),
    boxShadow: theme.shadows[1],
  },
}));

// --- Main Component ---

const TaskDetails = ({
  lotId, modal, onSave, setShowDialog, updateLot, setValidDelete, isClos
}: {
  lotId: string; modal: boolean; onSave: boolean;
  updateLot: Function; setShowDialog: Function;
  setValidDelete: Function;
  isClos: boolean;
}) => {
  const [showModal, setShowModal] = useState(false);
  const [modalId, setModalId] = useState<IModal | undefined>(undefined);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteId, setDeleteId] = useState<number>(0);

  // ÉTATS POUR LA MODAL (Ce qui manquait !)
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  const { width } = useWindowDimensions();
  const dispatch = useDispatch();
  const tasks = useSelector((state: RootState) => state.lotSynthesis.tasks) as ITask[];
  const token = useSelector((state: RootState) => state.user.token);
  // Récupère le projectId depuis l'URL /projet/:projectId
  const { projectId } = useParams<{ projectId: string }>();

  // Synchronisation de la Modal quand on clique sur une tâche
  useEffect(() => {
    const currentTask = tasks.find((t: ITask) => t._id === modalId?.task);
    if (currentTask && showModal) {
      setName(currentTask.name);
      setDescription(currentTask.description || "");
    }
  }, [modalId, showModal, tasks]);

  useEffect(() => { getLot(); }, [lotId]);

  useEffect(() => {
    if (tasks.length === 0) {
      dispatch(addTask());
      dispatch(calculAll());
      dispatch(calculTCosts());
    }
  }, [tasks, dispatch]);

  useEffect(() => { if (onSave) setShowDialog(false); }, [onSave, setShowDialog]);

  const getLot = () => {
    if (token) {
      getOne(token, "/lots/", lotId, (lot: ILot) => dispatch(initTasks(lot.tasks)), 
      () => dispatch(sessionEnd()));
    }
  };

  const handleAction = (key: string, taskId: string | number) => {
      if (["RTUD", "RTUI", "G"].includes(key)) {
        dispatch(calculAll());
      }
      dispatch(calculTCosts());
      setShowDialog(true);
  };

  const deleteTask = (index: number) => {
    if (token && tasks[index]) {
      const qp = projectId ? `?projectId=${projectId}` : "";
      fetchPutProtect(token, `/lots/${lotId}/task/${tasks[index]._id}${qp}`, null)
        .then(() => {
          // On retire la tâche du tableau AVANT de recalculer, sinon calculAll()
          // recalcule la synthèse des coûts en comptant encore la tâche supprimée
          // (DELETE_TASKS ne fait que retirer la tâche, il ne recalcule rien).
          dispatch(delete_tasks(index, tasks));
          dispatch(calculAll());
          dispatch(calculTCosts());
          setShowDialog(true);
          setConfirmDelete(false);
          setValidDelete(true);
        });
    }
  };

  const displayCell = (task: ITask) =>
    Object.keys(task).map((key) => {
      const val = task[key as keyof ITask];
      if (["_id", "PPR", "description"].includes(key)) return null;

      if (key === "name") {
        return (
          <StyledTableCell key={key} sx={{ minWidth: 200 }}>
            <Box
              onClick={() => { setModalId({ key, task: task._id }); setShowModal(true); }}
              sx={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                p: 1, cursor: "pointer", border: "1px solid #e2e8f0", borderRadius: "8px",
                '&:hover': { border: '1px solid #3b82f6', bgcolor: '#f8fafc' }
              }}
            >
              <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 150 }}>
                {val}
              </Typography>
              <HtmlTooltip title={<><strong>Description</strong><br/>{task.description}</>}>
                <InfoIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
              </HtmlTooltip>
            </Box>
          </StyledTableCell>
        );
      }

      const isEditable = ["RTUD", "RTUI", "A"].includes(key) && !isClos;

      return (
        <StyledTableCell key={key} sx={{ p: 0.5 }}>
          <InputField
            fullWidth
            variant="filled"
            name={key}
            value={val}
            disabled={!isEditable}  // ← déjà géré
            onChange={(e) => {
              if (isClos) return;  // ← sécurité supplémentaire
              const value = e.target.value;
              if (/^([0-9]+([\.,][0-9]*)?)?$/.test(value)) {
                dispatch(changeValue(value, key, task._id));
                handleAction(key, task._id);
              }
            }}
            onWheel={(e: any) => e.target.blur()}
            InputProps={{ disableUnderline: true }}
          />
        </StyledTableCell>
      );
    });

  return (
    <Box className="tasksTableContainer">
      <AlerteDialog
        open={confirmDelete}
        handleClose={() => setConfirmDelete(false)}
        deleteProject={() => deleteTask(deleteId)}
        title="Supprimer la tâche"
        contentText="Cette action supprimera définitivement la tâche et ses charges associées."
        color="error"
      />

      <Modal 
        open={showModal} 
        onClose={() => setShowModal(false)} 
        closeAfterTransition
      >
        <Fade in={showModal}>
          <Box sx={{ 
            p: 4, 
            bgcolor: 'white', 
            borderRadius: '20px', // Plus arrondi pour un look moderne
            maxWidth: 500, 
            mx: 'auto', 
            mt: '10%', 
            boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)', // Ombre plus douce
            border: '1px solid #e2e8f0'
          }}>
            <Typography variant="h6" mb={3} fontWeight={700} sx={{ color: '#1e293b' }}>
              Détails de la tâche
            </Typography>

            <Typography variant="caption" fontWeight={700} sx={{ color: '#64748b', mb: 1, display: 'block', textTransform: 'uppercase' }}>
              Nom de la tâche
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              sx={{ 
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                  bgcolor: '#f8fafc', // Même fond que l'en-tête du tableau
                  '& fieldset': { borderColor: '#e2e8f0' },
                  '&:hover fieldset': { borderColor: '#3b82f6' },
                }
              }}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />

            <Typography variant="caption" fontWeight={700} sx={{ color: '#64748b', mb: 1, display: 'block', textTransform: 'uppercase' }}>
              Description détaillée
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              placeholder="Décrivez les objectifs ou contraintes de cette tâche..."
              sx={{ 
                mb: 4,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                  bgcolor: '#f8fafc',
                  '& fieldset': { borderColor: '#e2e8f0' },
                  '&:hover fieldset': { borderColor: '#3b82f6' },
                }
              }}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />

            <Box sx={{ display: "flex", gap: 2 }}>
              <Button 
                fullWidth
                variant="text" 
                onClick={() => setShowModal(false)}
                sx={{ borderRadius: '10px', fontWeight: 600 }}
              >
                Annuler
              </Button>
              <Button 
                fullWidth
                variant="contained" 
                disableElevation
                disabled={isClos}
                onClick={() => {
                  dispatch(changeValue(name, "name", modalId?.task));
                  dispatch(changeValue(description, "description", modalId?.task));
                  setShowModal(false);
                  setShowDialog(true);
                }}
                sx={{ 
                  borderRadius: '10px', 
                }}
              >
                Enregistrer
              </Button>
            </Box>
          </Box>
        </Fade>
      </Modal>

      <TableContainer 
        component={Paper} 
        sx={{ 
          maxHeight: '70vh', 
          overflow: "auto",
          border: '1px solid #e2e8f0', 
          borderRadius: '12px' 
        }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <StyledTableCell>Tâches & Description</StyledTableCell>
              {["Concept.", "Real/Dev", "Real/Infra", "Qual/Stra", "Qual/CCT", "Qual/Exec", "Rec/Stra", "Rec/CCT", "Rec/Exec", "Deploy.", "Pil.&C.", "Acc", "Guar."].map((h) => (
                <StyledTableCell key={h} align="center">{h}</StyledTableCell>
              ))}
              <StyledTableCell align="center">Action</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tasks?.map((task: ITask, index: number) => (
              <TableRow key={task._id || index} hover>
                {displayCell(task)}
                <TableCell align="center">
                  {!isClos && (
                    <IconButton
                      size="small"
                      onClick={() => { setDeleteId(index); setConfirmDelete(true); }}
                      sx={{ color: '#ef4444', '&:hover': { bgcolor: '#fee2e2' } }}
                    >
                      <DeleteTwoToneIcon fontSize="small" />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default TaskDetails;